import React, { useEffect, useState } from "react";
import { Check, Loader2 } from "lucide-react";

import { getVisualProviderExecutionAdapterV1 } from "../../lib/brandStudioApi";

const WORKFLOW_LABELS = {
  photography_direction: "Фотография",
  material_direction: "Материалы / фактуры",
  graphic_presentation_direction: "Графический язык / подача",
};

const STATUS_LABELS = {
  true: "готов",
  false: "заблокирован",
};

const PACKING_TIER_LABELS = {
  primary: "прямой",
  supporting: "поддерживающий",
};

const formatUsd = (micros) =>
  typeof micros === "number" ? "$" + (micros / 1_000_000).toFixed(4) : "—";

function PacketCard({ packet }) {
  const selected = packet.reference_pack?.selected_for_provider || [];
  const deferredByRelevance = packet.reference_pack?.deferred_by_workflow_relevance || [];
  const deferred = packet.reference_pack?.deferred_by_provider_capacity || [];
  const capability = packet.capability || {};
  const privacy = packet.privacy || {};
  const pricing = packet.pricing || {};

  return (
    <article
      className="rounded-[16px] border border-[#DED0BC] bg-white p-4"
      data-testid={"pea1-packet-" + packet.workflow_type}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[9.5px] uppercase tracking-[0.14em] text-[#927A5B]">
            {WORKFLOW_LABELS[packet.workflow_type] || packet.workflow_type}
          </div>
          <div className="mt-2 text-[11px] leading-5 text-[#655141]">
            Prodia · FLUX.2 Klein 4B
          </div>
        </div>
        <span className="shrink-0 rounded-full bg-[#F3EBDD] px-2.5 py-1 text-[9px] text-[#6D5842]">
          {STATUS_LABELS[String(packet.packet_ready)]}
        </span>
      </div>

      <div className="mt-3 text-[10.5px] leading-5 text-[#655141]">
        Все референсы рабочего сценария: {packet.reference_pack?.workflow_reference_total || 0}
        <br />
        Релевантны задаче: {packet.reference_pack?.task_relevant_total || 0}
        <br />
        Отправляются в провайдер: {selected.length}
        <br />
        Отложено как нерелевантные этой задаче: {deferredByRelevance.length}
        <br />
        Отложено из-за лимита провайдера: {deferred.length}
        <br />
        Размер: {capability.width}×{capability.height}
        {" · "}шаги: {capability.steps}
        <br />
        Стилевой пресет: не используется
        <br />
        Опубликованная оценка: {formatUsd(pricing.published_estimate_micros)}
        <br />
        Аварийный лимит: {formatUsd(pricing.hard_cap_micros)}
      </div>

      <div className="mt-3 rounded-[11px] border border-[#EEE4D6] bg-[#FFFCF6] p-3 text-[9.5px] leading-4 text-[#7A624D]">
        Маршрут: {packet.route_allowed ? "разрешён" : "заблокирован"}
        {" · "}токен: {packet.token_configured ? "настроен" : "не настроен"}
        <br />
        Риск приватности для проверки владельца: {privacy.owner_qa_risk_accepted ? "принят" : "не принят"}
        <br />
        Использование для клиентов: не разрешено
        <br />
        Живой запуск: выключен
      </div>

      {!!packet.blocking_reasons?.length && (
        <div className="mt-3 rounded-[11px] border border-[#E2CFC8] bg-[#FFF4EF] p-3 text-[9.5px] leading-4 text-[#6B443A]">
          Блокирует: {packet.blocking_reasons.join(", ")}
        </div>
      )}

      <div className="mt-3 space-y-1.5">
        {selected.slice(0, 4).map((item) => (
          <div
            key={item.reference_id}
            className="rounded-[10px] border border-[#E6D9C8] px-3 py-2 text-[9.5px] text-[#6C5745]"
          >
            Ранг {item.workflow_rank}
            {" · "}
            {item.role_in_execution === "primary_visual_reference"
              ? "основной"
              : item.role_in_execution === "whole_style_reference"
                ? "целостный"
                : "поддерживающий"}
            {item.identity_content_present ? " · айдентика только как контекст" : ""}
          </div>
        ))}
        {selected.length > 4 && (
          <div className="text-[9.5px] text-[#927A5B]">
            + ещё {selected.length - 4} отправляемых референсов — в деталях
          </div>
        )}
      </div>
    </article>
  );
}

export default function VisualProviderExecutionAdapterOwnerPanelV1() {
  const [status, setStatus] = useState("loading");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    getVisualProviderExecutionAdapterV1()
      .then((value) => {
        if (cancelled) return;
        setResult(value);
        setStatus("ready");
      })
      .catch((requestError) => {
        if (cancelled) return;
        setError(
          requestError?.response?.data?.detail ||
            "Не удалось открыть предварительную проверку адаптера провайдера.",
        );
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section
      className="rounded-[24px] border border-dashed border-[#8C7359] bg-[#FFF8ED] p-5 md:p-7"
      data-testid="visual-provider-execution-adapter-owner-panel-v1"
    >
      <div className="text-[9.5px] uppercase tracking-[0.2em] text-[#8A6D53]">
        ПРОВЕРКА ВЛАДЕЛЬЦА · PEA1
      </div>
      <h3 className="mt-2 font-serif text-[27px] leading-tight text-[#2B211A]">
        Пакеты для Prodia
      </h3>
      <p className="mt-2 max-w-3xl text-[12.5px] leading-5 text-[#655141]">
        WE1 механически переводится в три отдельных provider-пакета. На этом этапе
        Prodia не вызывается, изображения наружу не отправляются и деньги не списываются.
      </p>

      {status === "loading" && (
        <div className="mt-5 inline-flex items-center gap-2 text-[12px] text-[#796652]">
          <Loader2 size={14} className="animate-spin" />
          Собираю preflight-пакеты…
        </div>
      )}

      {status === "error" && (
        <div className="mt-5 rounded-[13px] border border-[#E2CFC8] bg-[#FFF4EF] px-4 py-3 text-[12px] text-[#6B443A]">
          {String(error)}
        </div>
      )}

      {status === "ready" && result?.result === "not_ready" && (
        <div className="mt-5 rounded-[13px] border border-[#E2CFC8] bg-[#FFF4EF] px-4 py-3 text-[12px] text-[#6B443A]">
          {result.reason || "Сначала должен быть готов WE1."}
        </div>
      )}

      {!!result?.packets?.length && (
        <div className="mt-6 space-y-4 border-t border-[#E2D5C3] pt-5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EEF0DF] px-3 py-1.5 text-[10.5px] text-[#586044]">
              <Check size={12} /> 3 пакета для провайдера
            </span>
            <span className="text-[10.5px] text-[#8A745E]">
              живых вызовов: 0 · изображений наружу: 0 · расходов: 0
            </span>
          </div>

          <div className="grid gap-3 lg:grid-cols-3">
            {result.packets.map((packet) => (
              <PacketCard key={packet.workflow_type} packet={packet} />
            ))}
          </div>

          <details
            className="rounded-[14px] border border-[#DED0BC] bg-white"
            data-testid="pea1-provider-details"
          >
            <summary className="cursor-pointer px-4 py-3 text-[11.5px] font-medium text-[#4C392B]">
              Показать пакет провайдера
            </summary>
            <div className="border-t border-[#EEE4D6] px-4 py-4">
              <div className="grid gap-4 lg:grid-cols-3">
                {result.packets.map((packet) => (
                  <div key={packet.workflow_type}>
                    <div className="text-[10px] uppercase tracking-[0.12em] text-[#927A5B]">
                      {WORKFLOW_LABELS[packet.workflow_type] || packet.workflow_type}
                    </div>

                    <div className="mt-2 text-[9.5px] leading-4 text-[#796652]">
                      Версия адаптера: {packet.provider_adapter_version}
                      <br />
                      Модель: {packet.model}
                      <br />
                      Отпечаток пакета: {packet.packet_fingerprint}
                      <br />
                      Отпечаток задания: {packet.execution_fingerprint}
                      <br />
                      стилевой пресет: не передаётся
                      <br />
                      фактическая цена: ещё нет
                    </div>

                    <div className="mt-3 text-[9.5px] uppercase tracking-[0.12em] text-[#927A5B]">
                      Сериализованная инструкция
                    </div>
                    <pre className="mt-2 max-h-80 overflow-auto whitespace-pre-wrap rounded-[10px] bg-[#FAF6EF] p-3 text-[9px] leading-4 text-[#5F4A39]">
                      {packet.instruction?.text}
                    </pre>

                    <div className="mt-3 text-[9.5px] uppercase tracking-[0.12em] text-[#927A5B]">
                      Отправляемые референсы
                    </div>
                    <div className="mt-2 text-[9px] leading-4 text-[#796652]">
                      {(packet.reference_pack?.selected_for_provider || []).map((item) => (
                        <div key={item.reference_id}>
                          порядок {item.provider_order} · ранг сценария {item.workflow_rank} · {PACKING_TIER_LABELS[item.packing_tier] || item.packing_tier}
                          {item.identity_content_present ? " · айдентика только как контекст · копирование запрещено" : ""}
                        </div>
                      ))}
                    </div>

                    {!!packet.reference_pack?.deferred_by_workflow_relevance?.length && (
                      <>
                        <div className="mt-3 text-[9.5px] uppercase tracking-[0.12em] text-[#927A5B]">
                          Отложены как нерелевантные этой задаче
                        </div>
                        <div className="mt-2 text-[9px] leading-4 text-[#796652]">
                          {packet.reference_pack.deferred_by_workflow_relevance.map((item) => (
                            <div key={item.reference_id}>
                              ранг сценария {item.workflow_rank} · «Всё целиком» остаётся контекстом и не отправляется в узкий image workflow
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    {!!packet.reference_pack?.deferred_by_provider_capacity?.length && (
                      <>
                        <div className="mt-3 text-[9.5px] uppercase tracking-[0.12em] text-[#927A5B]">
                          Отложены из-за лимита провайдера
                        </div>
                        <div className="mt-2 text-[9px] leading-4 text-[#796652]">
                          {packet.reference_pack.deferred_by_provider_capacity.map((item) => (
                            <div key={item.reference_id}>rank {item.workflow_rank}</div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </details>
        </div>
      )}
    </section>
  );
}
